import { useId } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WarningBanner } from 'ui/banner';
import { FlexSpacer } from 'ui/flexSpacer';
import { DestructiveButton } from 'ui/button';
import { BoldText } from 'ui/theme';
import { selectUserProjectDefaultSenderEmail } from 'core/userContext';
import { ModalFooter, ModalBody, ModalHeader, Modal, MODAL_SIZES } from 'ui/modal';
import { DefaultSenderEmailForm } from './DefaultSenderEmailForm';
import type { AuthedEmailDeleteModalProps } from '../types';
import { deleteAndReplaceSenderRequest } from '../actions';
import {
  DefaultSenderEmailFormProvider,
  DefaultSenderEmailFormContextType,
} from '../contexts/DefaultSenderEmailFormProvider';

export const AuthedEmailDeleteModal = ({ authedEmail, onClose }: AuthedEmailDeleteModalProps) => {
  const dispatch = useDispatch();
  const id = useId();
  const formId = useId();
  const defaultSenderName = useSelector(selectUserProjectDefaultSenderEmail);

  const onSubmit = ({ senderName }: DefaultSenderEmailFormContextType) => {
    if (!senderName) {
      return;
    }

    dispatch(deleteAndReplaceSenderRequest({ newSender: senderName, email: authedEmail.email }));
    onClose();
  };

  return (
    <DefaultSenderEmailFormProvider defaultValues={{ senderName: defaultSenderName }}>
      <Modal onClose={onClose} isModalOpen id={id} size={MODAL_SIZES.SMALL}>
        <ModalHeader>Delete and replace sender</ModalHeader>
        <ModalBody>
          <FlexSpacer flexDirection="column" alignItems="stretch">
            <WarningBanner>
              The <BoldText>From email</BoldText> and <BoldText>From name</BoldText> fields of
              templates using the <BoldText>{authedEmail.email}</BoldText> sender will be
              overwritten by the newly sender selected. Ensure that the new sender is verified to
              send via the ESP for the associated channel, or your messages may stop sending.
            </WarningBanner>
            <DefaultSenderEmailForm formId={formId} onSubmit={onSubmit} />
          </FlexSpacer>
        </ModalBody>
        <ModalFooter onClose={onClose}>
          <DestructiveButton data-test="senders-delete-sender-action" type="submit" form={formId}>
            Delete
          </DestructiveButton>
        </ModalFooter>
      </Modal>
    </DefaultSenderEmailFormProvider>
  );
};
